import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const IDLE_TIMEOUT_MS = 10 * 60 * 1000;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const idleTimerRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("hotto_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("hotto_token"))
      .finally(() => setLoading(false));
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("hotto_token");
    setUser(null);
  }, []);

  // === Auto-logout 10 menit tidak aktif ===
  useEffect(() => {
    if (!user) return;
    const resetTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(async () => {
        toast.warning("Anda otomatis logout karena tidak aktif selama 10 menit", { duration: 6000 });
        await logout();
        window.location.href = "/login";
      }, IDLE_TIMEOUT_MS);
    };
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((ev) => window.addEventListener(ev, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, resetTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user, logout]);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    localStorage.setItem("hotto_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const role = user?.role;
  const hasRole = (...rs) => rs.includes(role);
  const canFinance = hasRole("owner", "finance");
  const canRetail = hasRole("owner", "manager_ops", "mitra");
  const canPOS = hasRole("owner", "kasir", "staff", "manager_ops");
  const isMitra = role === "mitra";
  const isInvestor = role === "investor";

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, role,
      isOwner: role === "owner",
      isKasir: role === "kasir",
      isFinance: role === "finance",
      isManagerOps: role === "manager_ops",
      isMitra, isInvestor,
      hasRole, canFinance, canRetail, canPOS,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
